import { BasicDataNode, DataNode, FieldDataNode } from "rc-tree/lib/interface";
import { FilePathType, SourceFile } from "../../types/SourceFiles";

export interface ExplorerDataNode extends BasicDataNode {
  key: string;
  title: React.ReactNode | ((data: ExplorerDataNode) => React.ReactNode);
  type: FilePathType;
  readOnly?: boolean;
  parentNode?: FieldDataNode<ExplorerDataNode>;
}

/**
 * Sorts the array nodes in place
 * @param nodes The nodes to be sorted in place
 */
export const sortNodes = (nodes: DataNode[] | undefined) => {
  if (!nodes)
    return;
  nodes.sort((a, b) => {
    if (!a.isLeaf && b.isLeaf) {
      return -1;
    } else if (!b.isLeaf && a.isLeaf) {
      return 1
    } else {
      if (a.title! > b.title!) {
        return 1;
      } else {
        return -1
      }
    }
  })
}

export const getTreeNodes = (files: SourceFile[]): FieldDataNode<ExplorerDataNode> => {

  const rootNode: FieldDataNode<ExplorerDataNode> = { key: "/", title: "/", type: "folder", children: [], isLeaf: false, readOnly: false };

  for (const file of files) {

    if (file.deleted)
      continue;

    //split path by "/" and remove empty segments
    const pathSegments = (file.filePathRenamed ?? file.filePath).split('/').filter((item) => item);
    const isLeaf = file.type === "file";

    const getNext = (node: FieldDataNode<ExplorerDataNode>, newNode: FieldDataNode<ExplorerDataNode>) => {
      node.children = node.children || [];
      const existingNode = node.children.find((item) => item.key === newNode.key)

      if (!existingNode) {
        node.children.push(newNode);
        sortNodes(node.children);
      }

      return existingNode ? existingNode : newNode;
    }

    let node = rootNode;
    let nodeKey = "";
    const lastSegment = pathSegments[pathSegments.length - 1];

    for (const segment of pathSegments) {
      nodeKey += `/${segment}`;
      node = getNext(node, {
        key: nodeKey,
        title: segment,
        type: segment === lastSegment ? file.type : "folder",
        isLeaf: segment === lastSegment && isLeaf,
        readOnly: file.readOnly,
        parentNode: node
      })
    }
  }

  return rootNode;
}

/**
 * Implementation of DFS (depth-first search) Algorithm to find the shortest path from a start to a target node.
 * Given a start node, this returns the node in the tree below the start node with the target value (or undefined if it doesn't exist)
 * Runs in O(n), where n is the number of nodes in the tree, or O(b^d), where b is the branching factor and d is the depth.
 *
 * @param start  the node to start the search from
 * @param key the value to search for
 * @return The node containing the key value or undefined if it doesn't exist.
 */
export const depthFirstSearch = (start: FieldDataNode<ExplorerDataNode>, key: string): FieldDataNode<ExplorerDataNode> | undefined => {
  const stack = [start];

  while (stack.length > 0) {
    const current = stack.pop()!;

    if (current.key === key) {
      // We have found the goal node we we're searching for
      return current;
    }

    if (current.children) {
      stack.push(...current.children)
    }
  }

  // We've gone through all nodes and not found the goal node
  return undefined;
}

export const getNestedNodes = (start: FieldDataNode<ExplorerDataNode>): FieldDataNode<ExplorerDataNode>[] => {
  const visited = [];
  const stack = [start];

  while (stack.length > 0) {
    const current = stack.pop()!;

    visited.push(current);

    if (current.children) {
      stack.push(...current.children)
    }
  }

  return visited;
}
